<template>
  <div class="cart-preview-wrapper">
    <Teleport to="body">
      <!-- Overlay Backdrop -->
      <Transition name="fade">
        <div v-if="isOpen" class="cart-preview-backdrop" @click="$emit('close')"></div>
      </Transition>

      <!-- Cart Drawer -->
      <Transition name="slide">
        <div 
          v-if="isOpen" 
          class="cart-preview" 
          @click.stop 
        >
          <div class="cart-header">
            <div class="header-content">
              <h3 class="cart-title">{{ t('header.cart.title').toUpperCase() }} ({{ cartCount }})</h3>
              <button v-if="cartItems.length > 0" class="btn-clear-cart-link" @click="handleClearCart" :disabled="isSyncing">
                {{ t('header.cart.clear_cart').toUpperCase() }}
              </button>
            </div>
            <button class="btn-close-drawer" @click="$emit('close')">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div v-if="cartItems.length > 0" class="cart-items-list custom-scrollbar" :class="{ 'single-item-layout': cartItems.length === 1 }">
            <div v-for="item in cartItems" :key="item.cartItemId || item.variantId || `${item.id}-${item.color}-${item.size}`" class="cart-item">
              <div class="item-image">
                <img :src="getImagePath(item.image)" :alt="item.name" class="cart-item-image" />
              </div>
              <div class="item-details">
                <div class="item-main-info">
                  <div class="item-name-wrapper">
                    <h4 class="item-name">{{ item.name }}</h4>
                    <button @click="handleRemoveFromCart(item.id, item.color, item.size, item.variantId, item.cartItemId)" class="btn-remove-item" title="Rimuovi">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                  </div>
                  <div class="item-variant-row">
                    <p class="item-variant">
                      <span v-if="item.size">{{ item.size }}</span>
                      <span v-if="item.size && item.color" class="separator"> | </span>
                      <span v-if="item.color">{{ item.color }}</span>
                    </p>
                  </div>
                  <div v-if="item.categoryTypeSku || item.sku" class="item-sku-row">
                    <span class="sku-label-text">COD.</span>
                    <span class="sku-value">{{ item.categoryTypeSku || item.sku }}</span>
                  </div>
                  <div class="item-price-wrapper">
                    <span class="item-price-small">{{ formatPrice(item.price) }}</span>
                    <template v-if="Number(item.oldPrice) > Number(item.price)">
                      <span class="item-old-price">{{ formatPrice(item.oldPrice) }}</span>
                      <span class="item-discount-badge">-{{ Math.round(item.discount || ((1 - Number(item.price) / Number(item.oldPrice)) * 100)) }}%</span>
                    </template>
                  </div>
                </div>
                
                <div class="item-footer">
                  <div class="quantity-selector">
                     <button @click="handleUpdateQuantity(item.id, item.color, item.size, item.quantity - 1, item.variantId, item.cartItemId)" class="qty-btn">−</button>
                     <span class="qty-value">{{ item.quantity }}</span>
                     <button @click="handleUpdateQuantity(item.id, item.color, item.size, item.quantity + 1, item.variantId, item.cartItemId)" class="qty-btn">+</button>
                  </div>
                  <span class="price-total">{{ formatPrice(item.price * item.quantity) }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="cartItems.length > 0" class="cart-footer">
              <div v-if="totalSavings > 0.01" class="cart-summary-details">
                <div class="summary-line">
                  <span class="label">{{ t('header.cart.list_price') }}</span>
                  <span class="amount">{{ formatPrice(listSubtotal) }}</span>
                </div>
                <div class="summary-line discount">
                  <span class="label">{{ t('header.cart.total_discount') }}</span>
                  <span class="amount">- {{ formatPrice(totalSavings) }}</span>
                </div>
              </div>

              <div class="subtotal-row">
                <span class="label">{{ t('header.cart.subtotal').toUpperCase() }}</span>
                <span class="amount">{{ formatPrice(subtotal) }}</span>
              </div>

              <div class="summary-line shipping-line">
                <span class="label">{{ t('header.cart.shipping').toUpperCase() }}</span>
                <span class="amount" :class="{ 'free-shipping': shipping === 0 }">
                  {{ shipping === 0 ? t('header.cart.free').toUpperCase() : formatPrice(shipping) }}
                </span>
              </div>

              <div class="grand-total-row">
                <span class="label">{{ t('header.cart.total').toUpperCase() }}</span>
                <div class="total-value-container">
                  <span class="amount">{{ formatPrice(total) }}</span>
                  <div class="price-vat-label-preview">{{ t('product.labels.vat_included') }}</div>
                </div>
              </div>
              <div class="action-buttons">
                <button class="btn-checkout" @click="handleCheckout" :disabled="isSyncing">
                  <span v-if="isSyncing">{{ t('header.cart.syncing') || 'SINCRONIZZAZIONE...' }}</span>
                  <span v-else>{{ t('header.cart.checkout').toUpperCase() }}</span>
                </button>

                <button class="btn-back-to-shop" @click="$emit('close')">
                  {{ t('header.cart.back_to_shop').toUpperCase() }}
                </button>
                
                <!-- Payment Trust Info -->
                <div class="payment-trust-info">
                  <!-- Divider Line -->
                  <div class="trust-divider"></div>

                  <!-- Payment Logos -->
                  <div class="payment-methods">
                    <div class="payment-methods-row">
                      <!-- Apple Pay -->
                      <div class="payment-method-card">
                        <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10.4 15.5c0-2.1 1.3-3.1 1.3-3.1-1-1.4-2.4-1.6-3-1.6-1.3-.1-2.4.7-3.1.7-.7 0-1.6-.7-2.7-.7-1.4 0-2.6.8-3.3 2.1-1.5 2.5-.4 6.2 1 8.3.7 1.1 1.5 2.1 2.5 2.1 1 0 1.4-.7 2.7-.7 1.2 0 1.6.7 2.7.7 1.1-.1 1.8-1 2.4-2 .8-1.2 1.1-2.3 1.1-2.3-.1 0-2.1-.8-2.1-3.5z" fill="black" transform="translate(4,0)"/>
                          <path d="M8.4 9.4c.6-.7.9-1.6.8-2.5-.8 0-1.8.6-2.4 1.3-.5.6-.9 1.6-.8 2.5.9.1 1.8-.5 2.4-1.3z" fill="black" transform="translate(4,0)"/>
                          <text x="32" y="18" font-family="Arial, sans-serif" font-size="9" text-anchor="middle" fill="black" font-weight="900">Pay</text>
                        </svg>
                      </div>
                      <!-- Google Pay -->
                      <div class="payment-method-card">
                        <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                          <text x="25" y="18" font-family="Arial, sans-serif" font-size="9" text-anchor="middle" fill="black" font-weight="900">G Pay</text>
                        </svg>
                      </div>
                      <!-- Visa -->
                      <div class="payment-method-card">
                        <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                          <text x="25" y="19" font-family="Arial, sans-serif" font-size="11" text-anchor="middle" fill="#1A1F71" font-weight="900" style="font-style: italic; letter-spacing: -0.5px;">VISA</text>
                        </svg>
                      </div>
                      <!-- Mastercard -->
                      <div class="payment-method-card">
                        <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="20" cy="12" r="7" fill="#EB001B"/>
                          <circle cx="30" cy="12" r="7" fill="#F79E1B"/>
                          <path d="M25 5.5a7 7 0 0 1 0 13 7 7 0 0 1 0-13z" fill="#FF5F00"/>
                          <text x="25" y="26" font-family="Arial, sans-serif" font-size="3.5" text-anchor="middle" fill="black" font-weight="bold">mastercard</text>
                        </svg>
                      </div>
                    </div>

                    <div class="payment-methods-row">
                      <!-- Maestro -->
                      <div class="payment-method-card">
                        <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="20" cy="12" r="7" fill="#0099CC" opacity="0.8"/>
                          <circle cx="30" cy="12" r="7" fill="#EB001B" opacity="0.8"/>
                          <text x="25" y="26" font-family="Arial, sans-serif" font-size="3.5" text-anchor="middle" fill="black" font-weight="bold">maestro</text>
                        </svg>
                      </div>
                      <!-- American Express -->
                      <div class="payment-method-card">
                        <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                          <rect x="8" y="7" width="34" height="16" fill="#007CC3"/>
                          <text x="25" y="18" font-family="Arial, sans-serif" font-size="7.5" text-anchor="middle" fill="white" font-weight="900">AMEX</text>
                        </svg>
                      </div>
                      <!-- PayPal -->
                      <div class="payment-method-card">
                        <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7 8h4c2.5 0 3.5 1 3.5 2.5s-2 3.5-4 3.5H8.5l-.8 4h-2L7 8z" fill="#003087"/>
                          <path d="M8.5 9.5h3c2.5 0 3.5 1 3.5 2.5s-1.5 2.5-3.5 2.5H9.5l-.8 4h-2L8.5 9.5z" fill="#0079C1" opacity="0.8"/>
                          <text x="31" y="18.5" font-family="Arial, sans-serif" font-size="8.5" text-anchor="middle" fill="#003087" font-weight="900" style="font-style: italic;">PayPal</text>
                        </svg>
                      </div>
                      <!-- Klarna -->
                      <div class="payment-method-card">
                        <svg viewBox="0 0 50 30" xmlns="http://www.w3.org/2000/svg">
                          <rect x="6" y="9" width="38" height="12" rx="6" fill="#FFB3C7"/>
                          <text x="25" y="17.8" font-family="Arial, sans-serif" font-size="8" text-anchor="middle" fill="black" font-weight="900">Klarna.</text>
                        </svg>
                      </div>
                    </div>
                  </div>

                  <!-- Trust Features -->
                  <div class="trust-features">
                    <div class="trust-feature-item">
                      <div class="feature-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                        </svg>
                      </div>
                      <div class="feature-text">
                        <span class="main-text">{{ t('header.cart.trust.secure_payment') }}</span>
                      </div>
                    </div>

                    <div class="trust-feature-item">
                      <div class="feature-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="1" y="3" width="15" height="13"></rect>
                          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                          <circle cx="5.5" cy="18.5" r="2.5"></circle>
                          <circle cx="18.5" cy="18.5" r="2.5"></circle>
                        </svg>
                      </div>
                      <div class="feature-text">
                        <span class="main-text">{{ t('header.cart.trust.standard_shipping') }}</span>
                        <span class="sub-text">{{ shipping === 0 ? t('header.cart.trust.free_over', { amount: configStore.freeShippingThreshold }) : t('header.cart.trust.delivery_time') }}</span>
                      </div>
                    </div>

                    <div class="trust-feature-item">
                      <div class="feature-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
                        </svg>
                      </div>
                      <div class="feature-text">
                        <span class="main-text">{{ t('header.cart.trust.returns') }}</span>
                        <span class="sub-text">{{ t('header.cart.trust.return_window') }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCart } from '@/composables/useCart'
import { useLanguage } from '@/composables/useLanguage'
import { useConfigStore } from '@/stores/configStore'
import { useToast } from '@/composables/useToast'

const { t } = useLanguage()
const configStore = useConfigStore()
const toast = useToast()

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  cartItems: {
    type: Array,
    required: true
  },
  subtotal: {
    type: Number,
    required: true
  },
  listSubtotal: {
    type: Number,
    required: true
  },
  totalSavings: {
    type: Number,
    required: true
  },
  cartCount: {
    type: Number,
    required: true
  },
  shipping: {
    type: Number,
    required: true
  },
  total: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['close'])
const router = useRouter()
const { updateQuantity, removeFromCart, clearCart, isSyncing } = useCart()

const handleClearCart = async () => {
  try {
    await clearCart()
  } catch (error) {
    toast.error(t('toast.error.generic'))
  }
}

const handleRemoveFromCart = async (itemId, color, size, variantId, cartItemId) => {
  try {
    await removeFromCart(itemId, color, size, variantId, cartItemId)
  } catch (error) {
    toast.error(t('toast.error.generic'))
  }
}

const handleUpdateQuantity = async (itemId, color, size, quantity, variantId, cartItemId) => {
  try {
    await updateQuantity(itemId, color, size, quantity, variantId, cartItemId)
  } catch (error) {
    toast.error(t('toast.error.generic'))
  }
}

// Handle scroll lock on body when cart is open
watch(() => props.isOpen, (isOpen) => {
  if (isOpen) {
    document.body.classList.add('modal-open')
    document.documentElement.classList.add('modal-open')
  } else {
    document.body.classList.remove('modal-open')
    document.documentElement.classList.remove('modal-open')
  }
})

onUnmounted(() => {
  document.body.classList.remove('modal-open')
  document.documentElement.classList.remove('modal-open')
})

const getImagePath = (imageName) => {
  if (!imageName) return ''
  if (imageName.startsWith('http') || imageName.startsWith('//')) return imageName
  if (imageName.startsWith('/')) return imageName
  if (imageName.includes('/')) return `/${imageName}`
  return `/images/products/${imageName}`
}

const formatPrice = (price) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR'
  }).format(price)
}

const handleCheckout = () => {
  emit('close')
  router.push('/carrello')
}
</script>

<style scoped>
.cart-preview-wrapper {
  display: contents;
}

.cart-preview-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  z-index: 19999; /* Superiore ai modal (10500) */
}

.cart-preview {
  position: fixed;
  top: 0;
  right: 0;
  width: min(480px, 100%);
  height: 100vh;
  height: 100dvh;
  background: white;
  z-index: 20000; /* Superiore al backdrop */
  display: flex;
  flex-direction: column;
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

@media (max-width: 1024px) {
  .cart-preview {
    width: min(420px, 100%);
  }
}

@media (max-width: 480px) {
  .cart-preview {
    width: 100%;
  }
}

.cart-header {
  padding: 10px 20px;
  border-bottom: 1px solid #f9f9f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.cart-title {
  margin: 0;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #000;
}

.btn-clear-cart-link {
  background: transparent;
  border: none;
  padding: 0;
  color: #888;
  font-size: 8px;
  font-weight: 500;
  letter-spacing: 1px;
  text-align: left;
  cursor: pointer;
  width: fit-content;
  transition: color 0.2s ease;
  text-decoration: none;
}

.btn-clear-cart-link:hover {
  color: #000;
}

.btn-clear-cart-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-close-drawer {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.btn-close-drawer:hover {
  opacity: 0.7;
  transform: scale(1.1);
}

.cart-items-list {
  flex: 1;
  overflow-y: auto !important;
  padding: 0 20px;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
}

/* Single Item Layout - Horizontal */
.cart-items-list.single-item-layout {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 20px;
}

.cart-items-list.single-item-layout .cart-item {
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  border-bottom: none;
  gap: 20px;
  padding: 15px 0;
  flex: 0 1 auto;
  width: 100%;
}

.cart-items-list.single-item-layout .item-image {
  width: 130px; /* Dimensione bilanciata per layout orizzontale */
  max-width: 130px;
  height: auto;
  aspect-ratio: 2/3;
  flex-shrink: 0;
}

.cart-items-list.single-item-layout .cart-item-image {
  object-fit: contain;
}

.cart-items-list.single-item-layout .item-details {
  align-items: flex-start;
  width: 100%;
  text-align: left;
  gap: 4px;
}

.cart-items-list.single-item-layout .item-main-info {
  align-items: flex-start;
}

.cart-items-list.single-item-layout .item-name-wrapper {
  justify-content: space-between;
  margin-bottom: 4px;
}

.cart-items-list.single-item-layout .item-name {
  font-size: 13px;
  letter-spacing: 1.5px;
}

.cart-items-list.single-item-layout .item-variant-row {
  align-items: flex-start;
  margin-bottom: 6px;
}

.cart-items-list.single-item-layout .item-variant {
  font-size: 10px;
}

.cart-items-list.single-item-layout .item-price-wrapper {
  justify-content: flex-start;
  margin-bottom: 8px;
}

.cart-items-list.single-item-layout .item-price-small {
  font-size: 14px;
}

.cart-items-list.single-item-layout .item-footer {
  width: 100%;
  justify-content: space-between;
  gap: 20px;
  margin-top: 10px;
}

.cart-items-list.single-item-layout .price-total {
  font-size: 14px;
}

.cart-items-list.single-item-layout .btn-remove-item {
  position: static;
  margin-left: 10px;
}

.cart-item {
  display: flex;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f5;
  align-items: flex-start;
  min-height: 0;
}

.item-image {
  width: 90px;
  height: auto;
  aspect-ratio: 2/3;
  flex-shrink: 0;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.item-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-height: 0;
  gap: 2px;
}

.cart-item-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: top center;
}

.item-name-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

.btn-remove-item {
  background: transparent;
  border: none;
  color: #ccc;
  cursor: pointer;
  padding: 0;
  margin-top: -1px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-remove-item:hover {
  color: #000;
}

.item-main-info {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.item-name {
  margin: 0 0 2px 0;
  font-size: 11px;
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  line-height: 1.2;
}

.item-variant-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  margin-bottom: 4px;
}

.item-variant {
  margin: 0;
  font-size: 10px;
  color: #444;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  font-weight: 600;
}

.separator {
  margin: 0 4px;
  color: #ccc;
  font-weight: 300;
}

.item-price-wrapper {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
}

.item-price-small {
  font-size: 12px;
  color: #000;
  font-weight: 700;
}

.item-old-price {
  font-size: 10px;
  color: #999;
  text-decoration: line-through;
  font-weight: 400;
}

.item-discount-badge {
  font-size: 10px;
  color: #d9534f;
  font-weight: 700;
}

.item-footer {
  margin-top: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.quantity-selector {
  display: flex;
  align-items: center;
  background: #fcfcfc;
  border: 1px solid #eeeeee;
  height: 28px;
  padding: 0 2px;
}

.qty-btn {
  width: 24px;
  height: 100%;
  border: none;
  background: transparent;
  color: #000;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}

.qty-btn:hover {
  opacity: 0.6;
}

.qty-value {
  width: 24px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #000;
}

.price-total {
  font-size: 13px;
  font-weight: 700;
  color: #000;
}

.cart-footer {
  padding: 15px 20px 20px;
  border-top: 1px solid #f5f5f5;
  background: #fff;
  flex-shrink: 0;
  position: relative;
  z-index: 10;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.05);
}

.savings-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
  color: #d9534f;
}

.savings-row .label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
}

.savings-row .amount {
  font-size: 12px;
  font-weight: 700;
}

.summary-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3px;
}

.summary-line .label {
  font-size: 11px;
  font-weight: 500;
  color: #888;
  letter-spacing: 0.8px;
  text-transform: uppercase;
}

.summary-line .amount {
  font-size: 11px;
  font-weight: 500;
  color: #888;
}

.summary-line.discount .label,
.summary-line.discount .amount {
  color: #d9534f;
  font-weight: 600;
}

.subtotal-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  margin-bottom: 3px;
  padding-top: 8px;
  border-top: 1px solid #f9f9f9;
}

.subtotal-row .label {
  font-size: 12px;
  font-weight: 700;
  color: #000;
  letter-spacing: 1.2px;
}

.subtotal-row .amount {
  font-size: 13px;
  font-weight: 700;
  color: #000;
}

.shipping-line {
  margin-bottom: 6px;
}

.shipping-line .label {
  font-size: 11px;
  font-weight: 500;
  color: #888;
  letter-spacing: 0.8px;
}

.shipping-line .amount {
  font-size: 11px;
  font-weight: 500;
  color: #888;
}

.shipping-line .amount.free-shipping {
  color: #000;
  font-weight: 700;
}

.grand-total-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 10px;
  padding-top: 10px;
  border-top: 1px solid #000;
  gap: 16px;
}

.grand-total-row .label {
  font-size: 14px;
  font-weight: 800;
  color: #000;
  letter-spacing: 1.5px;
}

.total-value-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: right;
}

.grand-total-row .amount {
  font-size: 18px;
  font-weight: 800;
  color: #000;
}

.price-vat-label-preview {
  font-size: 9px;
  color: #888;
  font-weight: 400;
  letter-spacing: 0.5px;
  text-transform: none;
  margin-top: 2px;
}

.action-buttons {
  margin-top: 2px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.btn-checkout {
  width: 100%;
  padding: 12px 20px;
  border: none;
  background: #000;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-checkout:hover {
  background: #333;
}

.btn-checkout:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-back-to-shop {
  width: 100%;
  background: transparent;
  color: #999;
  border: none;
  padding: 6px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0px;
  text-decoration: underline;
  text-underline-offset: 5px;
}

.btn-back-to-shop:hover {
  color: #000;
}

.payment-trust-info {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  width: 100%;
}

.trust-divider {
  width: 100%;
  height: 1px;
  background-color: #f5f5f5;
  margin-bottom: 10px;
}

.payment-methods {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin-bottom: 10px;
  width: 100%;
}

.payment-methods-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
}

.payment-method-card {
  width: 60px;
  height: 36px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.payment-method-card:hover {
  border-color: #000;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.payment-method-card svg {
  width: 100%;
  height: 100%;
  display: block;
}

.trust-features {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 15px;
}

.trust-feature-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  text-decoration: none;
  color: #000;
  transition: all 0.3s ease;
}

.trust-feature-item:hover {
  transform: translateY(-2px);
}

.feature-icon {
  margin-bottom: 8px;
  color: #000;
}

.feature-icon svg {
  width: 22px;
  height: 22px;
}

.feature-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-height: 32px;
  justify-content: flex-start;
  width: 100%;
}

.main-text {
  display: block;
  font-size: 9px;
  font-weight: 700;
  line-height: 1.3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sub-text {
  display: block;
  font-size: 9px;
  color: #666;
  font-weight: 400;
  line-height: 1.3;
}

.empty-cart {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 30px;
}

.empty-text {
  font-size: 12px;
  color: #333;
  font-weight: 500;
  letter-spacing: 1px;
  text-align: center;
}

/* Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.4s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

.slide-enter-active, .slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.slide-enter-from, .slide-leave-to {
  transform: translateX(100%);
}

/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 2px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #fff;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #eaeaea;
}

.item-sku-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  margin-top: 2px;
}

.sku-label-text {
  font-size: 8px;
  color: #bbb;
  font-weight: 500;
  letter-spacing: 1px;
}

.sku-value {
  background: transparent;
  color: #888888;
  font-family: 'Courier New', Courier, monospace;
  font-size: 11px;
  letter-spacing: 1px;
  text-transform: uppercase;
  padding: 0;
  margin: 4px 0;
}
</style>
